import {Component, HostListener, OnInit, ViewChild} from '@angular/core';
import { Trigger } from '../../../model/trigger';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TriggersService } from '../../../services/triggers.service';
import {ActivatedRoute, Router} from '@angular/router';
import { TriggerPersistRequest } from '../../../model/requests/trigger-persist-request';
import { DeviceMeasurement } from '../../../model/device-measurement';
import { AssetMeasurement } from '../../../model/asset-measurement';
import { noSpecialCharacters, noWhitespaces } from '../../../shared/custom-validators';
import { TenantsService } from '../../../services/tenants.service';
import { ComponentCanDeactivate } from '../../../common/can-deactivate/component-can-deactivate';
import { LoggedUser } from '../../../common/logged-user.service';
import { CommonMessagesService } from '../../../services/common-messages.service';
import {Organization} from '../../../model/organization';
import {Tenant} from '../../../model/tenant';
import {AuthGuard} from '../../../common/auth-guard.service';
import {DeviceFilters} from '../../../model/filters/device-filters';
import {
  DeviceMeasurementsSelectorComponent
} from '../../shared/device-measurements-selector/device-measurements-selector.component';
import {
  AssetMeasurementsSelectorComponent
} from '../../shared/asset-measurements-selector/asset-measurements-selector.component';
import {TranslocoService} from '@ngneat/transloco';
import {EntityWithUuid} from '../../../model/custom/entity-with-uuid';

@Component({
  selector: 'app-triggers-form',
  templateUrl: './triggers-form.component.html',
  styleUrls: ['./triggers-form.component.scss']
})
export class TriggersFormComponent implements OnInit, ComponentCanDeactivate {

  @ViewChild('deviceMeasurementsSelector')
  deviceMeasurementsSelector: DeviceMeasurementsSelectorComponent;

  @ViewChild('assetMeasurementsSelector')
  assetMeasurementsSelector: AssetMeasurementsSelectorComponent;

  trigger: Trigger;
  types: any[];

  triggerForm: FormGroup;
  persistingChanges: any;
  formSuccess: boolean;

  tenantSelected: boolean;
  organizationSelected: boolean;
  tenants: Tenant[];
  organizations: Organization[];
  tenant: Tenant;
  organization: Organization;
  filters: DeviceFilters;

  displayDeviceMeasurementSelector: boolean;
  displayAssetMeasurementSelector: boolean;
  deviceMeasurementSelection: DeviceMeasurement;
  assetMeasurementSelection: AssetMeasurement;
  entityUuid: EntityWithUuid;

  mode: 'create' | 'edit';
  editInfo: { oldName: string };

  constructor(private formBuilder: FormBuilder,
              private route: ActivatedRoute,
              private router: Router,
              private loggedUser: LoggedUser,
              private tenantsService: TenantsService,
              private commonMessagesService: CommonMessagesService,
              private triggersService: TriggersService,
              private authGuard: AuthGuard,
              private i18n: TranslocoService) {

    this.i18n.selectTranslation().subscribe(() => {
      this.types = [
        {label: this.i18n.translate('trigger.operation.lt'), value: 'LT'},
        {label: this.i18n.translate('trigger.operation.lte'), value: 'LTE'},
        {label: this.i18n.translate('trigger.operation.gt'), value: 'GT'},
        {label: this.i18n.translate('trigger.operation.gte'), value: 'GTE'},
        {label: this.i18n.translate('trigger.operation.eq'), value: 'EQ'},
        {label: this.i18n.translate('trigger.operation.byteMask'), value: 'BYTE_MASK'},
        {label: this.i18n.translate('trigger.operation.inactivity'), value: 'INACTIVITY'}
      ];
    });

    this.filters = new DeviceFilters();
  }

  public ngOnInit(): void {

    this.route.url.subscribe(urlSegment => {
      if (urlSegment[0].path === 'new') {
        this.mode = 'create';
        this.loadCreationForm(urlSegment[1]?.path, urlSegment[2]?.path, urlSegment[3]?.path, urlSegment[4]?.path);
      } else {
        this.mode = 'edit';

        this.route.params.subscribe(params =>
          this.entityUuid = (params?.fromEntity?.toLowerCase() === 'true' ? {uuid: urlSegment[1].path} as EntityWithUuid : undefined)
        );

        this.loadEditionForm(urlSegment[0].path, urlSegment[1].path, urlSegment[2].path, urlSegment[3].path);
      }
    });
  }
  loadCreationForm(entityType?: string, entityUuid?: string, tenantUuid?: string, organizationUuid?: string): void {
    this.trigger = new Trigger();

    this.trigger.entityType = entityType !== undefined ? entityType : 'DEVICE';

    this.createForm(this.trigger);

    if (entityType) {
      this.loadFormFromDevice(entityUuid, tenantUuid, organizationUuid);
    } else {
      if (this.authGuard.superAdminRequired()) {
        this.tenantsService.getTenantSummaries().toPromise().then(response => {
          this.tenants = response.tenants;
        });
      } else {
        this.tenants = this.loggedUser.userTenants;
        this.tenant = this.tenants[0];
        this.triggerForm.get('tenant').setValue(this.tenant);
        this.triggerForm.get('tenant').disable();
        this.loadOrganizations(this.tenant.uuid).then(() => {
          this.enableTenantDependentElements();
          if (this.tenants.length === 1 && this.organizations.length === 1) {
            this.triggerForm.get('organization').setValue(this.organizations[0]);
            this.triggerForm.get('organization').disable();
          }
        });
      }
    }
  }

  private loadFormFromDevice(entityUuid: string, tenantUuid: string, organizationUuid: string): void {
    if (this.authGuard.superAdminRequired()) {
      this.tenantsService.getTenantSummaries().toPromise().then(response => {
        this.tenants = response.tenants;
        this.tenant = this.tenants.filter(tenant => tenant.uuid === tenantUuid)[0];
        this.setLoadCreationData(organizationUuid);
      });
    } else {
      this.tenants = this.loggedUser.userTenants;
      this.tenant = this.tenants.filter(tenant => tenant.uuid === tenantUuid)[0];
      this.setLoadCreationData(organizationUuid);
    }

    this.entityUuid = {uuid: entityUuid} as EntityWithUuid;
  }

  private setLoadCreationData(organizationUuid: string): void {
    this.triggerForm.get('tenant').setValue(this.tenant);
    this.triggerForm.get('entityType').disable();
    this.triggerForm.get('tenant').disable();

    this.loadOrganizations(this.tenant.uuid).then(() => {
      this.enableTenantDependentElements();
      this.organization = this.organizations.filter(organization => organization.uuid === organizationUuid)[0];
      this.triggerForm.get('organization').setValue(this.organization);
      this.triggerForm.get('organization').disable();
    });
  }

  private loadEditionForm(entityType: string, entityUuid: string, entityMeasurementUuid: string, triggerName: string): void {
    this.triggersService.getTrigger(entityType, entityUuid, entityMeasurementUuid, triggerName).toPromise().then(response => {
      this.trigger = response.trigger;

      this.editInfo = {oldName: this.trigger.name};
      if (entityType === 'DEVICE') {
        this.deviceMeasurementSelection = this.trigger.deviceMeasurement;
      } else {
        this.assetMeasurementSelection = this.trigger.assetMeasurement;
      }

      this.createForm(this.trigger);
    });
  }

  private createForm(trigger: Trigger): void {

    const tenant = this.tenant ? {name: this.tenant.name, uuid: this.tenant.uuid} : null;
    const organization = this.organization ? {name: this.organization.name, uuid: this.organization.uuid} : null;
    this.triggerForm = this.formBuilder.group({

      name: [trigger.name, [Validators.maxLength(255), Validators.required, noSpecialCharacters, noWhitespaces]],
      expression: [trigger.expression, [Validators.maxLength(255), Validators.required]],
      type: [trigger.type, [Validators.maxLength(255), Validators.required]],
      ruleType: [this.mode === 'create' ? 'SYSTEM' : (trigger.ownerUuid ? 'USER' : 'SYSTEM'), Validators.required],
      category: [this.mode === 'create' ? 'ALARM' : trigger.category, Validators.required],
      disabledRepeatedEvents: [this.mode === 'create' ? false : trigger.disabledRepeatedEvents, Validators.required],
      entityType: [trigger.entityType, Validators.required],
      ownerUuid: [this.loggedUser.user.username, Validators.required],
      tenant: [tenant],
      organization: [{value: organization, disabled: true}]

    });
    this.triggerForm.markAsPending();

    if (this.mode === 'edit') {
      this.triggerForm.get('name').disable();
      this.triggerForm.get('entityType').disable();
    }
  }

  public entityTypeChanged(event: any): void {
    this.deviceMeasurementSelection = undefined;
    this.assetMeasurementSelection = undefined;
    this.deviceMeasurementsSelector?.clearSelection();
    this.assetMeasurementsSelector?.clearSelection();
  }

  public onTenantChange(event: any): void {
    if (event.value) {
      this.deviceMeasurementSelection = undefined;
      this.assetMeasurementSelection = undefined;
      this.deviceMeasurementsSelector?.clearSelection();
      this.assetMeasurementsSelector?.clearSelection();
      this.tenantSelected = true;
      this.organizationSelected = false;
      this.triggerForm.get('organization').setValue(null);
      this.loadOrganizations(event.value.uuid).then(() => {
        this.enableTenantDependentElements();
      });
    }
  }

  public onOrganizationChange(event: any): void {
    if (event?.value) {
      const selectedDeviceIsFromSameOrg =
        this.triggerForm.get('entityType').value === 'DEVICE' &&
        this.deviceMeasurementSelection?.device.organization?.uuid === event.value.uuid;
      const selectedAssetIsFromSameOrg =
        this.triggerForm.get('entityType').value === 'ASSET' &&
        this.deviceMeasurementSelection?.device.organization?.uuid === event.value.uuid;
      if (!selectedDeviceIsFromSameOrg) {
        this.deviceMeasurementSelection = undefined;
        this.deviceMeasurementsSelector?.clearSelection();
      }
      if (!selectedAssetIsFromSameOrg) {
        this.assetMeasurementSelection = undefined;
        this.assetMeasurementsSelector?.clearSelection();
      }
      this.organizationSelected = true;
    }
  }

  public onClearOrganization(): void {
    this.triggerForm.get('organization').setValue(undefined);
  }

  private async loadOrganizations(tenantUuid: string): Promise<any> {
    this.disableTenantDependentElements();
    const response = await Promise.all([
      this.tenantsService.getTenantOrganizationSummaries(tenantUuid).toPromise(),
    ]);
    this.organizations = response[0].organizations;
  }

  private enableTenantDependentElements(): void {
    this.tenantSelected = true;
    this.triggerForm.get('organization').enable();
  }

  private disableTenantDependentElements(): void {
    this.tenantSelected = false;
    this.triggerForm.get('organization').disable();
  }

  public persistTriggerData(): void {
    if (!this.triggerForm.valid) {
      return;
    }

    this.persistingChanges = true;
    const persistData = this.buildPersistData();

    let promise: Promise<any>;
    if (this.mode === 'create') {
      promise = this.triggersService.createTrigger(persistData).toPromise();
    } else {
      promise = this.triggersService.editTrigger(persistData).toPromise();
    }

    promise.then(() => {
      this.formSuccess = true;
      this.router.navigate(this.entityUuid !== undefined ? ['/devices', this.entityUuid.uuid, 'details'] : ['/alarms/triggers']);
    }).catch((error: any) => {
      this.commonMessagesService.showPersistingError(this.mode, error, persistData.name);
      this.persistingChanges = false;
    });
  }

  private buildPersistData(): TriggerPersistRequest {
    const formValue = this.triggerForm.getRawValue();
    return {
      triggerRule: null,
      name: formValue.name,
      expression: formValue.expression,
      type: formValue.type,
      entityType: formValue.entityType,
      deviceUuid: formValue.entityType === 'DEVICE' ? this.deviceMeasurementSelection.device.uuid : null,
      deviceMeasurementUuid: formValue.entityType === 'DEVICE' ? this.deviceMeasurementSelection.uuid : null,
      assetUuid: formValue.entityType === 'ASSET' ? this.assetMeasurementSelection.asset.uuid : null,
      assetMeasurementUuid: formValue.entityType === 'ASSET' ? this.assetMeasurementSelection.uuid : null,
      ownerUuid: formValue.ruleType === 'USER' ? formValue.ownerUuid : null,
      oldName: this.mode === 'edit' ? this.editInfo.oldName : null,
      category: formValue.category,
      disabledRepeatedEvents: formValue.disabledRepeatedEvents
    };
  }

  @HostListener('window:beforeunload')
  public canDeactivate(): boolean {
    return this.formSuccess || this.triggerForm.untouched;
  }

  public onTypeChange(event: any): void {
    if (event.value === 'BYTE_MASK') {
      this.triggerForm.get('expression').setValue(0);
    }
  }

}
